import type { KeyboardShortcut } from '@/config/keyboard-shortcuts'
import type { ReactNode } from 'react'
import { useEffect } from 'react'
import { DialogPattern } from '@/components/ui/dialog-pattern'
import { KEYBOARD_SHORTCUT_GROUPS } from '@/config/keyboard-shortcuts'
import { cn } from '@/lib/utils'
import { isTauri } from '@/lib/utils/tauri'
import { useKeyboardShortcutsStore } from '@/store/keyboard-shortcuts.store'
import {
  formatShortcutKey,
  formatShortcutKeys,
  shouldIgnoreKeyboardEvent,
} from '@/utils/keyboard-utils'

/** A single keycap. Quiet, bordered, sized to sit on a 13px text line. */
function Keycap({ children }: { children: ReactNode }) {
  return (
    <kbd className="inline-flex h-5 min-w-5 items-center justify-center rounded border border-border bg-muted/60 px-1.5 font-sans text-[11px] font-medium leading-none text-muted-foreground">
      {children}
    </kbd>
  )
}

function ShortcutRow({ shortcut }: { shortcut: KeyboardShortcut }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1">
      <span className="text-[13px] leading-snug text-foreground/80">
        {shortcut.label}
      </span>
      <span className="flex shrink-0 items-center gap-1">
        {shortcut.keys.map((key, index) => (
          <Keycap key={`${key}-${index}`}>{formatShortcutKey(key)}</Keycap>
        ))}
        {shortcut.altKeys && (
          <>
            <span className="px-0.5 text-[11px] text-muted-foreground">or</span>
            {shortcut.altKeys.map((key, index) => (
              <Keycap key={`alt-${key}-${index}`}>
                {formatShortcutKey(key)}
              </Keycap>
            ))}
          </>
        )}
      </span>
    </div>
  )
}

/**
 * The "?" cheatsheet. Lists only shortcuts that are genuinely wired — see
 * config/keyboard-shortcuts.ts. Desktop-only entries (global OS hotkeys
 * registered by Tauri) are filtered out in the browser.
 *
 * Motion: nothing beyond the shared Dialog's own enter/exit, so the app's
 * prefers-reduced-motion handling needs no special case here.
 */
export function KeyboardShortcutsDialog() {
  const isOpen = useKeyboardShortcutsStore(state => state.isOpen)
  const setShortcutsOpen = useKeyboardShortcutsStore(
    state => state.setShortcutsOpen,
  )

  // Global OS-level hotkeys only exist in the Tauri shell, so hide them on web.
  const isDesktop = isTauri()
  const groups = KEYBOARD_SHORTCUT_GROUPS.map(group => ({
    ...group,
    shortcuts: group.shortcuts.filter(
      shortcut => isDesktop || !shortcut.desktopOnly,
    ),
  })).filter(group => group.shortcuts.length > 0)

  return (
    <DialogPattern
      open={isOpen}
      onOpenChange={setShortcutsOpen}
      size="lg"
      title="Keyboard shortcuts"
      description={
        <>
          Press{' '}
          <kbd className="inline-flex h-4 items-center rounded border border-border bg-muted/60 px-1 font-sans text-[10px] font-medium">
            ?
          </kbd>{' '}
          anywhere outside a text field to bring this back.
        </>
      }
      footer={null}
    >
      <div className="columns-1 gap-8 sm:columns-2 [&>*]:break-inside-avoid">
        {groups.map(group => (
          <section key={group.title} className="mb-6">
            <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {group.title}
            </h3>
            {group.hint && (
              <p className="mb-1.5 text-[11px] leading-snug text-muted-foreground">
                {group.hint}
              </p>
            )}
            <div className="divide-y divide-border/50 border-t border-border/50">
              {group.shortcuts.map(shortcut => (
                <ShortcutRow key={shortcut.label} shortcut={shortcut} />
              ))}
            </div>
          </section>
        ))}
      </div>
    </DialogPattern>
  )
}

/**
 * Headless global listener for "?" (Shift+/). Mounted once by the app shell,
 * alongside the other global key handling. Deliberately ignores the key while
 * focus is in a text field and while any other dialog/menu is open, so "?"
 * stays typeable and never steals a modal's Escape.
 */
export function KeyboardShortcutsHotkey() {
  const openShortcuts = useKeyboardShortcutsStore(state => state.openShortcuts)
  const isOpen = useKeyboardShortcutsStore(state => state.isOpen)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '?') return
      // '?' is Shift+/ — a Cmd/Ctrl/Alt combo belongs to something else.
      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (event.defaultPrevented) return
      // Typing in an input, textarea, contenteditable, or an open Radix
      // popover/select surface.
      if (shouldIgnoreKeyboardEvent(event)) return
      if (isOpen) return
      // Another modal owns the screen — don't stack on top of it.
      if (
        document.querySelector(
          '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]',
        )
      ) {
        return
      }

      event.preventDefault()
      openShortcuts()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, openShortcuts])

  return null
}

/**
 * Inline shortcut hint for tooltips, e.g. "Search ⌘K" / "Search Ctrl+K".
 * Only use it on a control the shortcut genuinely triggers.
 */
export function TooltipShortcutHint({
  keys,
  className,
}: {
  keys: string[]
  className?: string
}) {
  return (
    <span className={cn('ml-1.5 font-medium opacity-60', className)}>
      {formatShortcutKeys(keys)}
    </span>
  )
}
